import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * ティッカースライダーの初期化
 */
const initializeTickerSlider = () => {
  const tickerSelector = '.js-ticker .splide';

  if (document.querySelector(tickerSelector)) {
    const tickerOptions = {
      lazyLoad: 'nearby',
      mediaQuery: 'min',
      fixedWidth: '15rem',
      gap: 16,
      type: 'loop',
      arrows: false,
      drag: 'free',
      flickPower: 300,
      pagination: false,
      autoScroll: {
        speed: 0.5,
        pauseOnHover: false,
        pauseOnFocus: false,
      },
      breakpoints: {
        1025: {
          gap: 32,
          fixedWidth: '25rem',
        },
      },
    };

    const tickerSplide = new Splide(tickerSelector, tickerOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    tickerSplide.on('mounted', () => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    tickerSplide.mount({ AutoScroll });
  }
};

/**
 * 通常スライダーの初期化（シームレス拡大・縮小対応フル版）
 * - 移動開始時から「入ってくるスライド」を拡大
 * - 「左端から外れるスライド」は縮みながら退場
 * - 外部の前/次ボタン（#tlsPrev / #tlsNext）で操作
 * - Splide が付与する不要な role を削除（アクセシビリティ調整）
 */
const initializeSlider = () => {
  const sliderSelector = '.js-slider .splide';

  const root = document.querySelector(sliderSelector);
  if (!root) return;

  const sliderOptions = {
    // type: 'fade',
    // rewind: true,
    type: 'loop',
    perPage: 4,
    perMove: 1,
    arrows: false,
    updateOnMove: true,

    breakpoints: {
      1024: { perPage: 3 }, // 1024px以下
      768: { perPage: 2 }, // 768px以下（タブレット/小さめ）
      480: { perPage: 1 }, // 480px以下（SP）
    },
  };

  const sliderSplide = new Splide(sliderSelector, sliderOptions);

  // ===== 外部ナビ（存在チェック付き） =====
  const prevBtn = document.getElementById('tlsPrev');
  const nextBtn = document.getElementById('tlsNext');
  if (prevBtn) prevBtn.addEventListener('click', () => sliderSplide.go('<'));
  if (nextBtn) nextBtn.addEventListener('click', () => sliderSplide.go('>'));

  // ===== アクセシビリティ微調整（不要な role 削除） =====
  const listSelector = `${sliderSelector} .splide__list`;
  const observer = new MutationObserver(() => {
    const splideList = document.querySelector(listSelector);
    if (splideList && splideList.hasAttribute('aria-roledescription')) {
      splideList.removeAttribute('aria-roledescription');
      splideList.removeAttribute('role');
    }
  });

  // ===== シームレス拡大用クラス制御 =====
  const CLASS_EARLY = 'is-active-early'; // 移動開始～確定までの仮アクティブ
  const CLASS_ENTER = 'is-entering'; // 入場中（拡大）
  const CLASS_LEAVE = 'is-leaving'; // 離脱中（縮小）

  // 便利: すべての <li.splide__slide> を取得
  const getAllSlideEls = () => sliderSplide.Components?.Elements?.slides || [];

  // 便利: インデックスから Slide オブジェクト → .slide(=Element) を取得
  const getSlideElAt = (index) => {
    const s = sliderSplide.Components?.Slides?.getAt(index);
    return s && s.slide ? s.slide : null;
  };

  // 一時クラスの全消し
  const clearTempClasses = () => {
    getAllSlideEls().forEach((el) => {
      el.classList.remove(CLASS_EARLY, CLASS_ENTER, CLASS_LEAVE);
    });
  };

  // 指定スライドを「入場中」に
  const markEntering = (index) => {
    const el = getSlideElAt(index);
    if (!el) return;
    el.classList.add(CLASS_EARLY, CLASS_ENTER);
    el.classList.remove(CLASS_LEAVE);
  };

  // 指定スライドを「離脱中」に
  const markLeaving = (index) => {
    const el = getSlideElAt(index);
    if (!el) return;
    el.classList.add(CLASS_EARLY, CLASS_LEAVE);
    el.classList.remove(CLASS_ENTER);
  };

  // ===== ★ 追記ここから：クローン要素にも適用（prev端対策） =====
  // 実際にレンダリングされている <li>（クローン含む）を直接扱う版
  const getRenderedSlides = () => sliderSplide.Components?.Elements?.list?.children || [];

  const clearTempClassesRendered = () => {
    Array.from(getRenderedSlides()).forEach((el) => {
      el.classList.remove(CLASS_EARLY, CLASS_ENTER, CLASS_LEAVE);
    });
  };

  const markEnteringEl = (el) => {
    if (!el) return;
    el.classList.add(CLASS_EARLY, CLASS_ENTER);
    el.classList.remove(CLASS_LEAVE);
  };

  const markLeavingEl = (el) => {
    if (!el) return;
    el.classList.add(CLASS_EARLY, CLASS_LEAVE);
    el.classList.remove(CLASS_ENTER);
  };

  // === クローンに対しても拡大・縮小を同期 ===
  sliderSplide.on('active', (Slide) => {
    // これからアクティブになる実要素（クローンのこともある）
    markEnteringEl(Slide.slide);
  });

  sliderSplide.on('inactive', (Slide) => {
    // アクティブから外れる実要素
    markLeavingEl(Slide.slide);
  });

  // ====== イベント束ね ======
  sliderSplide.on('mounted', () => {
    // a11y 調整
    const splideList = document.querySelector(listSelector);
    if (splideList) {
      observer.observe(splideList, { attributes: true });
      // 初回も一応削除
      if (splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    }

    // 初期状態：現在の先頭を入場中扱い（＝すでに拡大）
    clearTempClasses();
    markEntering(sliderSplide.index);
  });

  // スライド移動開始：旧→離脱 / 新→入場
  sliderSplide.on('move', (newIndex, oldIndex /*, destIndex */) => {
    // 旧スライドは縮みながら退場
    if (typeof oldIndex === 'number') {
      markLeaving(oldIndex);
    } else {
      // oldIndex が来ないケースの保険として全消し
      clearTempClasses();
    }
    // 新スライドは拡大しながら入場
    markEntering(newIndex);
  });

  // ドラッグ（つまんで移動中）：現在インデックスに追随して先行状態を更新
  // ※ドラッグは連発するので最小限の更新に留める
  let draggingRaf = 0;
  sliderSplide.on('drag', () => {
    if (draggingRaf) return;
    draggingRaf = requestAnimationFrame(() => {
      draggingRaf = 0;
      // ドラッグ中は現在 index を入場側にしておく（見た目のズレを抑える）
      clearTempClasses();
      markEntering(sliderSplide.index);
    });
  });

  // 完了時：Splide 標準の .is-active が正になるので、仮クラスを掃除
  const onEnd = () => {
    clearTempClasses();
    clearTempClassesRendered();
  };
  sliderSplide.on('moved', onEnd);
  sliderSplide.on('dragged', onEnd);

  // 破棄時は監視も解除（念のため）
  sliderSplide.on('destroy', () => {
    observer.disconnect();
  });

  // ===== マウント =====
  sliderSplide.mount();
};

/**
 * Splide 全体の初期化
 */
const initializeSplide = () => {
  initializeTickerSlider();
  initializeSlider();
};

/**
 * モジュールの初期化
 */
const init = () => {
  initializeSplide();
};

export default { init };
